// src/components/ui/PDFViewer.js
import React, { useState, useEffect } from "react";
import { Upload, ChevronDown, ChevronUp } from "lucide-react";

export default function PDFViewer({
    pdfUrl,
    fileName,
    scrollTargetId = "resume-form",  // Which element to track
    headerOffset = 120               // Distance from top when sticky
}) {
    const [isExpanded, setIsExpanded] = useState(false);
    const [offsetTop, setOffsetTop] = useState(headerOffset);
    const [isSticky, setIsSticky] = useState(false);

    useEffect(() => {
        const form = document.querySelector(`#${scrollTargetId}`);
        if (!form) return;

        const HEADER_OFFSET = headerOffset; // Use prop value

        const handleScroll = () => {
            const rect = form.getBoundingClientRect();
            const formTop = window.scrollY + rect.top;
            const formBottom = formTop + rect.height;

            const viewportHeight =
                window.innerHeight || document.documentElement.clientHeight;
            const scrollY = window.scrollY;
            const viewerHeight = viewportHeight - 160;

            const alignmentFix = HEADER_OFFSET;
            const topStop = formTop - HEADER_OFFSET + alignmentFix;
            const bottomStop = formBottom - viewerHeight - HEADER_OFFSET;

            if (scrollY >= topStop && scrollY <= bottomStop) {
                setOffsetTop(HEADER_OFFSET);
                setIsSticky(true);
            } else if (scrollY < topStop) {
                const distance = topStop - scrollY;
                setOffsetTop(HEADER_OFFSET + distance);
                setIsSticky(false);
            } else {
                const distance = scrollY - bottomStop;
                setOffsetTop(HEADER_OFFSET - distance);
                setIsSticky(false);
            }
        };

        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        window.addEventListener("resize", handleScroll);
        return () => {
            window.removeEventListener("scroll", handleScroll);
            window.removeEventListener("resize", handleScroll);
        };
    }, [scrollTargetId, headerOffset]);

    const pdfUrlWithZoom = pdfUrl ? `${pdfUrl}#zoom=100` : null;

    // ===== Empty state =====
    if (!pdfUrl) {
        return (
            <>
                <div className="block lg:hidden w-full mb-6">
                    <div className="bg-white rounded-3xl shadow-xl p-8 min-h-[200px] flex items-center justify-center">
                        <div className="text-center text-gray-400">
                            <Upload className="w-10 h-10 mx-auto mb-3 opacity-50" />
                            <p className="text-base">Upload a PDF to preview it here</p>
                        </div>
                    </div>
                </div>

                <div
                    className="hidden lg:block"
                    style={{
                        position: "fixed",
                        top: `${offsetTop}px`,
                        left: 0,
                        width: "49%",
                        zIndex: 5,
                        transition: isSticky ? "none" : "transform 0.2s ease-out",
                        paddingLeft: "1rem",
                        paddingRight: "1rem",
                    }}
                >
                    <div
                        className="bg-white rounded-3xl shadow-xl flex items-center justify-center"
                        style={{ height: "calc(100vh - 160px)", minHeight: "500px" }}
                    >
                        <div className="text-center">
                            <Upload className="w-12 h-12 lg:w-16 lg:h-16 mx-auto mb-4 text-[var(--forest-green)] opacity-50" />
                            <p className="text-base lg:text-lg text-[var(--forest-green)]">
                                Upload a PDF to preview it here
                            </p>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // ===== With PDF =====
    return (
        <>
            {/* Mobile */}
            <div className="lg:hidden w-full mb-6">
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="w-full bg-[var(--forest-green)] text-white px-6 py-4 flex items-center justify-between"
                    >
                        <div className="flex items-center gap-3">
                            <h3 className="font-bold text-lg">PDF Preview</h3>
                            {fileName && (
                                <span
                                    className="text-sm opacity-90 truncate max-w-[150px]"
                                    title={fileName}
                                >
                                    {fileName}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">
                                {isExpanded ? "Hide" : "Show"}
                            </span>
                            {isExpanded ? (
                                <ChevronUp className="w-5 h-5" />
                            ) : (
                                <ChevronDown className="w-5 h-5" />
                            )}
                        </div>
                    </button>

                    {isExpanded && (
                        <div className="relative bg-gray-100" style={{ height: "70vh" }}>
                            <iframe
                                src={pdfUrlWithZoom}
                                className="w-full h-full border-0"
                                title="Resume Preview"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Desktop - Fixed PDF viewer that follows scroll */}
            <div
                className="hidden lg:block"
                style={{
                    position: "fixed",
                    top: `${offsetTop}px`,
                    left: 0,
                    width: "49%",
                    zIndex: 5,
                    transition: isSticky ? "none" : "transform 0.2s ease-out",
                    paddingLeft: "1rem",
                    paddingRight: "1rem",
                }}
            >
                <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                    <div className="bg-[var(--forest-green)] text-white px-6 py-4 flex items-center justify-between">
                        <h3 className="font-bold text-lg">PDF Preview</h3>
                        {fileName && (
                            <span
                                className="text-sm opacity-90 truncate ml-4 max-w-[220px]"
                                title={fileName}
                            >
                                {fileName}
                            </span>
                        )}
                    </div>
                    <div
                        className="relative bg-gray-100"
                        style={{
                            height: "calc(100vh - 200px)",
                            overflow: "auto"
                        }}
                    >
                        <iframe
                            src={pdfUrlWithZoom}
                            className="w-full h-full border-0"
                            title="Resume Preview"
                            style={{ minHeight: "100%" }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}